/**
 * 画面の数値入力を、保存してよい数に直す。
 *
 * `min` は入力欄のヒントで、貼り付けやプログラム的な変更、
 * そして保存処理そのものは止めない。実際に
 *   席の追加料金 -5000 円 / 最小人数 -5 人 / 必要人数 -3 名
 * がそのまま Firestore に保存されていた。
 * 追加料金がマイナスだと、誤入力が会計で値引きとして通る。
 * 必要人数がマイナスだと、日別ビューが Array.from({length}) で
 * 展開するため募集がガントから丸ごと消える。
 *
 * 「弾く」のか「丸める」のかは呼び出し側で決める。
 * 金額のように誤りを黙って直すと危ないものは validate 系を使い、
 * 表示の並び順のように直してよいものは clamp 系を使う。
 */

/** 0 以上の数か。金額・料率に使う。 */
export function isNonNegative(v: unknown): boolean {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0;
}

/** 1 以上の整数か。人数・件数に使う。 */
export function isPositiveInt(v: unknown): boolean {
  const n = Number(v);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 1;
}

/** 範囲に収める。並び順など、黙って直してよいものだけに使う。 */
export function clamp(v: unknown, min: number, max: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

/**
 * パーセント表記の入力を、保存する率（0〜1）に直す。
 *
 * 画面は「10」と入れて 0.1 で保存する形が多い。
 * `inputProps={{max:100}}` を付けていても、保存ボタンが form の submit で
 * なければ HTML5 の検証は走らず、200 がそのまま通る。
 *
 * 実際に商品カテゴリの利益控除率で 200 が保存され、
 * profitDeductionRate = 2 になっていた。
 * backCalculation.ts の `(1 - effRate)` が -1 になり、
 * Math.max(0, 負) で 0。そのカテゴリの売上が
 * 全キャストのバック計算で丸ごと 0 になる。エラーは出ない。
 *
 * 100 を超える率を黙って丸めると「入れた値と違う率で計算される」ので、
 * 画面側で弾くのが正しい。この関数は最後の砦として、
 * 範囲外なら既定値に倒す。
 */
export function pctToRate(v: unknown, fallbackPct = 0): number {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0 || n > 100) return fallbackPct / 100;
  return n / 100;
}

/** 率（0〜1）として妥当か。保存する直前の確認に使う。 */
export function isValidRate(v: unknown): boolean {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 1;
}
